<!--
 * Path: src/core/components/floatingPanel/FloatingPanelListItem.vue
 * Task: Component controller resolving reactive layout properties and size adjustments.
-->
<template>
  <!-- STREAMING_CHUNK:Rendering the wrapper... -->
  <div
    class="floating-panel-list-item py-3 d-flex align-start w-100 position-relative"
    :class="{ 'cursor-pointer': clickable, 'item-hover': clickable }"
    style="width: 100%;"
    @click="handleClick"
  >
    <!-- Left Accent Bar representing entity color status -->
    <div
      v-if="accentColor"
      class="accent-bar"
      :style="{ backgroundColor: accentColor }"
    ></div>

    <!-- STREAMING_CHUNK:Rendering the inner content... -->
    <!-- Inner content wrapper maintaining ideal padding -->
    <div class="d-flex align-start w-100" style="width: 100%; padding-left: 16px; padding-right: 16px;">
      <!-- Avatar / Icon with customizable size -->
      <v-avatar
        v-if="avatarText || icon"
        :color="avatarColor"
        :size="parsedSize"
        class="me-3 flex-shrink-0"
      >
        <span class="text-white font-weight-bold" :style="{ fontSize: fontSize }">
          {{ avatarText || '' }}
        </span>
      </v-avatar>

      <!-- Main content (Title, Subtitle & default slot) -->
      <div class="flex-grow-1 min-w-0 d-flex flex-column w-100" style="min-width: 0; width: 100%;">
        <!-- High Contrast Color assigned dynamically from vuetifyConfig.ts to match description title -->
        <span
          v-if="title"
          class="text-subtitle-2 font-weight-bold text-truncate w-100 mb-1"
          style="width: 100%; color: rgb(var(--v-theme-hud-text)) !important;"
        >
          {{ title }}
        </span>

        <!-- Secondary High Contrast Color assigned dynamically from vuetifyConfig.ts -->
        <span
          v-if="subtitle"
          class="text-body-2 text-truncate w-100"
          style="width: 100%; color: rgb(var(--v-theme-hud-text-secondary)) !important; opacity: 0.85;"
        >
          {{ subtitle }}
        </span>

        <!-- Default slot to render textarea inputs or multiline comment bodies -->
        <slot></slot>
      </div>

      <!-- Optional actions slot on the right side -->
      <div v-if="$slots.append" class="d-flex align-center flex-shrink-0 ms-auto ps-3" style="gap: 8px;">
        <slot name="append"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* STREAMING_CHUNK:Setting up props and logic... */
import { computed } from 'vue';

interface Props {
  title?: string;
  subtitle?: string;
  icon?: string;
  iconColor?: string;
  avatarText?: string;
  avatarColor?: string;
  avatarSize?: number | string;
  clickable?: boolean;
  accentColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  icon: '',
  iconColor: 'white',
  avatarText: '',
  avatarColor: 'primary',
  avatarSize: 36,
  clickable: false,
  accentColor: ''
});

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const parsedSize = computed<number>(() => {
  const s = parseInt(String(props.avatarSize), 10);
  return isNaN(s) ? 36 : s;
});

const fontSize = computed<string>(() => {
  const s = parsedSize.value;
  if (s <= 20) return '9px';
  if (s <= 24) return '11px';
  if (s <= 32) return '13px';
  return '14px';
});

const handleClick = (e: MouseEvent) => {
  if (props.clickable) {
    emit('click', e);
  }
};
</script>

<style scoped>
/* STREAMING_CHUNK:Styling the list item... */
.floating-panel-list-item {
  border-bottom: 1px solid rgba(var(--v-theme-hud-border), 0.1);
  transition: background-color 0.2s ease;
}

.floating-panel-list-item:last-child {
  border-bottom: none;
}

.item-hover:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.accent-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  z-index: 1;
}
</style>
